import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveContract,
  createContract,
  fetchContractCounts,
  fetchContractDetail,
  fetchContractPriceTypes,
  fetchContracts,
  markSignedContract,
  renewContract,
  returnContract,
  submitForApproval,
  updateContractTerms,
  uploadContractAttachment,
  uploadMainDocument,
} from "@/api/contracts";
import type {
  ContractApprovePayload,
  ContractAttachmentUploadPayload,
  ContractCreatePayload,
  ContractListQuery,
  ContractMainDocumentPayload,
  ContractMarkSignedPayload,
  ContractRenewPayload,
  ContractReturnPayload,
  ContractTermsUpdatePayload,
} from "@/api/types";

const LIST_STALE = 30_000;
const DETAIL_STALE = 30_000;

/**
 * F4 §E — invalidate after every mutation. Mirrors F2/F3 helpers.
 */
function invalidateF4(
  qc: ReturnType<typeof useQueryClient>,
  contractId?: string | null,
) {
  qc.invalidateQueries({ queryKey: ["contracts", "list"] });
  qc.invalidateQueries({ queryKey: ["homeSummary"] });
  if (contractId) {
    qc.invalidateQueries({ queryKey: ["contracts", "detail", contractId] });
  }
}

export function useContractList(q: ContractListQuery = {}) {
  const key = [
    "contracts",
    "list",
    q.view ?? "all",
    q.search ?? "",
    q.cursor ?? null,
    q.offset ?? 0,
    q.source ?? "",
    q.statusGroup ?? "",
    q.kormaxLe ?? "",
    q.usageStatus ?? "",
    q.archived ?? "exclude",
  ] as const;
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchContracts(q, signal),
    staleTime: LIST_STALE,
  });
}

/** WS-1 — canonical counts snapshot (KPI/tabs/chips; backend-owned numbers). */
export function useContractCounts() {
  return useQuery({
    queryKey: ["contracts", "counts"],
    queryFn: ({ signal }) => fetchContractCounts(signal),
    staleTime: LIST_STALE,
  });
}

export function useContractDetail(id: string | null) {
  return useQuery({
    queryKey: ["contracts", "detail", id],
    queryFn: ({ signal }) => fetchContractDetail(id!, signal),
    enabled: !!id,
    staleTime: DETAIL_STALE,
  });
}

/** Contract UNIFICATION — BI price-type reference (stable; cached for the session). */
export function useContractPriceTypes(enabled = true) {
  return useQuery({
    queryKey: ["contracts", "price-types"],
    queryFn: ({ signal }) => fetchContractPriceTypes(signal),
    staleTime: 10 * 60_000,
    enabled,
  });
}

/** Contract UNIFICATION — save editable commercial terms on a crm_managed draft. */
export function useUpdateContractTerms(contractId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractTermsUpdatePayload) =>
      updateContractTerms(contractId!, body),
    onSuccess: () => invalidateF4(qc, contractId),
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractCreatePayload) => createContract(body),
    onSuccess: (data) => invalidateF4(qc, data.id),
  });
}

export function useUploadMainDocument(contractId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractMainDocumentPayload) =>
      uploadMainDocument(contractId!, body),
    onSuccess: () => invalidateF4(qc, contractId),
  });
}

export function useSubmitForApproval(contractId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => submitForApproval(contractId!),
    onSuccess: () => invalidateF4(qc, contractId),
  });
}

export function useApprove(contractId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractApprovePayload) => approveContract(contractId!, body),
    onSuccess: () => invalidateF4(qc, contractId),
  });
}

export function useReturn(contractId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractReturnPayload) => returnContract(contractId!, body),
    onSuccess: () => invalidateF4(qc, contractId),
  });
}

export function useMarkSigned(contractId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractMarkSignedPayload) =>
      markSignedContract(contractId!, body),
    onSuccess: () => invalidateF4(qc, contractId),
  });
}

export function useRenew(contractId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractRenewPayload) => renewContract(contractId!, body),
    onSuccess: (data) => {
      invalidateF4(qc, contractId);
      if (data.newContractId) {
        invalidateF4(qc, data.newContractId);
      }
    },
  });
}

export function useUploadContractAttachment(contractId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractAttachmentUploadPayload) =>
      uploadContractAttachment(contractId!, body),
    onSuccess: () => invalidateF4(qc, contractId),
  });
}
